/**
 * <frame-picker data-size="A4 (21 x 29,7 cm)" data-size-radio-name="cp-option1">
 *   <ul>
 *     <li><button class="frame-picker__tile"
 *                 data-frame-code="none|<colour code>"
 *                 data-overlay-src="<url>"
 *                 data-variants='{"A4":111,"A3":112,...}'
 *                 data-deltas='{"A4":"129 kr",...}'
 *                 aria-pressed="true|false">…</button></li>
 *     ...
 *   </ul>
 * </frame-picker>
 *
 * One tile per frame COLOUR. Frames are individual products — one per colour AND size — so a
 * tile's data-variants maps a SIZE CODE to the variant of that colour's product in that size.
 * The colour code is the tail of an R1-<SIZE><COLOUR> SKU ('S' sort, 'H' hvid, 'E' lys eg,
 * 'GR' gammelrosa, …); see snippets/element.frame-picker.liquid for how the maps are built.
 *
 * There is no orientation dimension: one A4 frame fits a portrait and a landscape A4 poster.
 *
 * Maintains state (selected frame colour + current size). On click:
 *   - flips aria-pressed across tiles
 *   - dispatches CustomEvent 'frame:change' with { frameCode, frameVariantId, overlaySrc, deltaText }
 *   - looks for the page's <frame-stack> and updates its data-frame attribute
 *
 * Listens for 'submit' at capture phase on the closest ancestor <form>, and ONLY acts when that
 * form carries a [data-photo-file-input] holding a file — i.e. the custom-photo PDP. Any other
 * form submits normally and is never intercepted. On the custom-photo path it preventDefault()s,
 * posts the baked photo as multipart then the frame as JSON, rolls the photo back if the frame
 * add fails, and notifies the cart drawer.
 */
(function () {
  class FramePicker extends HTMLElement {
    connectedCallback() {
      this.tiles = Array.from(this.querySelectorAll('.frame-picker__tile'));
      this.tiles.forEach(t => t.addEventListener('click', this.onTileClick.bind(this)));
      this.selectedLabel = this.querySelector('[data-frame-picker-selected]');
      this.updateSelectedLabel();

      // Listen for size changes on the page-level radios (if name provided).
      const radioName = this.getAttribute('data-size-radio-name');
      if (radioName) {
        this.sizeChangeHandler = (e) => {
          if (e.target && e.target.name === radioName) {
            this.setAttribute('data-size', e.target.value);
            this.refreshSelectedDelta();
            this.refreshHiddenInputForCurrentSelection();
          }
        };
        document.addEventListener('change', this.sizeChangeHandler);
      }

      // Registered at capture phase — harmless/defensive, but note that when the event target
      // IS this form, listeners on that same node fire in registration order regardless of the
      // capture flag, so capture alone does NOT guarantee running before another listener bound
      // to the same form. This is safe here for a different reason: theme.AjaxProduct (assets/
      // theme.js) is only ever constructed for a section carrying data-section-type=
      // "product-template", bound to a form with id AddToCartForm-<sectionId> (see theme.js
      // ~line 3751 and ~lines 3924-3926). The custom-photo section declares neither attribute
      // nor that form id, so AjaxProduct never binds to this form and there is no competing
      // handler to outrun.
      this.formSubmitHandler = this.onFormSubmit.bind(this);
      // The picker is only ever rendered nested INSIDE the custom-photo form. No document-level
      // fallback on purpose: if it is not inside a form it binds nothing, so it can never
      // intercept an unrelated add-to-cart form elsewhere on the page.
      this.boundForm = this.closest('form');
      if (this.boundForm) {
        this.boundForm.addEventListener('submit', this.formSubmitHandler, true);
      }
    }

    disconnectedCallback() {
      if (this.boundForm && this.formSubmitHandler) {
        this.boundForm.removeEventListener('submit', this.formSubmitHandler, true);
      }
      if (this.sizeChangeHandler) {
        document.removeEventListener('change', this.sizeChangeHandler);
      }
    }

    get selectedTile() {
      return this.tiles.find(t => t.getAttribute('aria-pressed') === 'true') || null;
    }

    get selectedCode() {
      const t = this.selectedTile;
      return t ? t.getAttribute('data-frame-code') : 'none';
    }

    get currentSize() {
      return this.getAttribute('data-size') || '';
    }

    // The size radios carry the poster's own label ("A4 (21 x 29,7 cm)"); the frame maps are
    // keyed by the size code out of the SKU ("A4"). Reducing the label to its leading token is
    // what keeps the two from drifting apart — matching the full label exactly is how this
    // used to miss whenever a merchant reworded a size.
    static sizeCodeOf(value) {
      const first = String(value == null ? '' : value).trim().split(/[\s(]/)[0];
      return first ? first.toUpperCase() : '';
    }

    onTileClick(e) {
      const tile = e.currentTarget;
      this.tiles.forEach(t => t.setAttribute('aria-pressed', t === tile ? 'true' : 'false'));

      const code = tile.getAttribute('data-frame-code');
      const overlaySrc = tile.getAttribute('data-overlay-src') || '';
      const variantId = this.variantIdFor(tile, this.currentSize);
      const deltaText = this.deltaTextFor(tile, this.currentSize);

      // Update the page-level <frame-stack>. The picker and stack are NOT siblings —
      // the stack lives in a [data-frame-stack-host] container in the gallery column
      // while the picker is inside the buy-buttons block. Look up by attribute.
      const host = document.querySelector('[data-frame-stack-host]');
      const stack = host ? host.querySelector('frame-stack') : document.querySelector('frame-stack');
      if (stack) stack.setAttribute('data-frame', code || 'none');

      // Update hidden frame variant input, if any.
      this.refreshHiddenInputForCurrentSelection();

      // Show the selected frame's name (+ price) beside the legend.
      this.updateSelectedLabel();

      this.dispatchEvent(new CustomEvent('frame:change', {
        bubbles: true,
        detail: { frameCode: code, frameVariantId: variantId, overlaySrc: overlaySrc, deltaText: deltaText }
      }));
    }

    variantIdFor(tile, size) {
      const code = FramePicker.sizeCodeOf(size);
      if (!tile || !code) return null;
      const raw = tile.getAttribute('data-variants');
      if (!raw) return null;
      try {
        const map = JSON.parse(raw);
        return map[code] != null ? map[code] : null;
      } catch (err) {
        console.warn('[frame-picker] bad data-variants JSON', err);
        return null;
      }
    }

    deltaTextFor(tile, size) {
      const code = FramePicker.sizeCodeOf(size);
      if (!tile || !code) return '';
      const raw = tile.getAttribute('data-deltas');
      if (!raw) return '';
      try {
        const map = JSON.parse(raw);
        return map[code] != null ? map[code] : '';
      } catch (err) {
        return '';
      }
    }

    refreshSelectedDelta() {
      // Size changed → the selected frame is a different product, at a different price.
      this.updateSelectedLabel();
    }

    updateSelectedLabel() {
      // Name only — no "(+129,00 kr)" price suffix (merchant request). The delta still
      // rides the frame:change event for anything that needs it programmatically.
      if (!this.selectedLabel) return;
      const tile = this.selectedTile;
      if (!tile) { this.selectedLabel.textContent = ''; return; }
      const name = tile.getAttribute('data-frame-name') || '';
      this.selectedLabel.textContent = name ? '— ' + name : '';
    }

    refreshHiddenInputForCurrentSelection() {
      const form = this.closest('form');
      if (!form) return;
      const input = form.querySelector('[data-frame-variant-input]');
      if (!input) return;
      const tile = this.selectedTile;
      const code = tile ? tile.getAttribute('data-frame-code') : 'none';
      if (code === 'none' || !tile) { input.value = ''; return; }
      const id = this.variantIdFor(tile, this.currentSize);
      input.value = id ? String(id) : '';
    }

    onFormSubmit(event) {
      const tile = this.selectedTile;
      const code = tile ? tile.getAttribute('data-frame-code') : 'none';
      const form = event.currentTarget;
      const photoFileInput = form.querySelector('[data-photo-file-input]');
      const isCustomPhotoForm = !!(photoFileInput && photoFileInput.files && photoFileInput.files.length > 0);

      // A frame may or may not be selected — the custom-photo form takes the managed path
      // either way, because the photo upload cannot go through the theme's default handler.

      // Only the custom-photo form is ever intercepted. Anything else submits normally.
      if (!isCustomPhotoForm) return;

      // The custom-photo form must NEVER fall through to a native submit, even when the frame
      // selection can't be resolved to a variant ID — a native submit here would navigate the
      // page away and skip the managed photo upload entirely. So prevent default first, then
      // bail out (surfacing the failure to the customer) if the variant ID lookup failed.
      event.preventDefault();
      event.stopImmediatePropagation();

      const variantId = (code && code !== 'none') ? this.variantIdFor(tile, this.currentSize) : null;
      if (code && code !== 'none' && !variantId) {
        // Nothing was added to the cart on this path — unlike the mid-flow rollback failure
        // below, "try again" would just send the customer round a loop that can never
        // succeed for this frame/size combo. Use the dedicated "unavailable" copy instead.
        console.warn('[frame-picker] no frame variant ID for size', this.currentSize);
        this.showCartError(form, 'cart_error_frame_unavailable');
        return;
      }

      this.submitCustomPhotoForm(form, variantId);
    }

    async submitCustomPhotoForm(form, frameVariantId) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      this.setSubmitBusy(form, true);
      try {
        // Step 1 — photo (multipart, includes the file)
        const photoResp = await fetch(root + 'cart/add.js', {
          method: 'POST',
          body: new FormData(form)
        });
        if (!photoResp.ok) {
          const text = await photoResp.text();
          throw { stage: 'photo', detail: text };
        }
        const photoLine = await photoResp.json();
        const photoKey = photoLine && photoLine.key;

        // Step 2 — frame (only if one was selected)
        if (frameVariantId) {
          const frameResp = await fetch(root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items: [{ id: Number(frameVariantId), quantity: 1 }] })
          });
          if (!frameResp.ok) {
            // Rollback: remove the photo line item we just added.
            if (photoKey) {
              await fetch(root + 'cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify({ id: photoKey, quantity: 0 })
              }).catch(err => console.warn('[frame-picker] rollback failed', err));
            }
            const text = await frameResp.text();
            throw { stage: 'frame', detail: text };
          }
        }

        // A successful add must not leave a stale error from a previous failed attempt
        // showing (e.g. the customer retried after a rollback and it worked this time).
        this.hideCartError(form);

        // Success. This theme's CartDrawer listens for the jQuery event 'added.ajaxProduct'
        // (assets/theme.js), not the native one, so fire that to refresh and open the drawer.
        // The native event is kept for any other subscriber.
        form.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: { product: photoLine },
          bubbles: true
        }));
        if (window.jQuery) {
          window.jQuery('body').trigger('added.ajaxProduct', window.jQuery(form).find('[data-add-to-cart]'));
        }
      } catch (err) {
        console.warn('[frame-picker] cart add failed', err);
        this.showCartError(form, err && err.stage === 'frame'
          ? 'cart_error_frame'
          : 'cart_error_photo');
      } finally {
        this.setSubmitBusy(form, false);
      }
    }

    setSubmitBusy(form, busy) {
      const btn = form.querySelector('[data-add-to-cart]');
      if (!btn) return;
      btn.disabled = busy;
      btn.classList.toggle('is-busy', busy);
      form.classList.toggle('is-busy', busy);
    }

    // Cart-add errors (frame failed, rollback state unknown, unsupported browser) must land
    // somewhere the customer can actually see at the moment they can click add-to-cart —
    // which is exactly when <photo-cropper> (and its banner) is hidden. So this looks for a
    // dedicated [data-cart-error] host near the form FIRST, and only falls back to the
    // cropper banner (still correct for upload/validation errors, since the cropper IS
    // visible during upload) or alert() if no host exists at all.
    cartErrorHost(form) {
      const scope = (form && (form.closest('section') || form.ownerDocument)) || document;
      return scope.querySelector('[data-cart-error]');
    }

    showCartError(form, localeKey) {
      const strings = (window.theme && window.theme.strings && window.theme.strings.customPhoto) || {};
      const fallbackMessages = {
        cart_error_frame: "We couldn't add the frame. Please check your basket before trying again.",
        cart_error_frame_unavailable: "That frame isn't available in the selected size. Please choose a different frame or size.",
        cart_error_unsupported_browser: "Your browser is out of date and can't complete this order. Please update it or use a different browser.",
        cart_error_photo: "Couldn't upload your photo. Please try again."
      };
      const message = strings[localeKey] || fallbackMessages[localeKey] || fallbackMessages.cart_error_photo;

      const errorHost = this.cartErrorHost(form);
      if (errorHost) {
        errorHost.textContent = message;
        errorHost.hidden = false;
        errorHost.setAttribute('role', 'alert');
        return;
      }

      // No dedicated host on the page — fall back to the cropper banner if present.
      const cropper = document.querySelector('photo-cropper');
      const banner = cropper && cropper.querySelector('[data-photo-cropper-banner]');
      if (banner) {
        banner.textContent = message;
        banner.className = 'photo-cropper__banner is-error';
        banner.hidden = false;
        banner.setAttribute('role', 'alert');
        return;
      }
      console.warn('[frame-picker] no error host found; falling back to alert');
      alert(message);
    }

    hideCartError(form) {
      const errorHost = this.cartErrorHost(form);
      if (errorHost) {
        errorHost.hidden = true;
        errorHost.textContent = '';
      }
    }
  }

  if (!customElements.get('frame-picker')) {
    customElements.define('frame-picker', FramePicker);
  }
})();
